/**
 * /hospitality/gaming/markers — Markers & Credit + progressive jackpot liability.
 *
 * Mirrors a casino's credit desk: outstanding patron markers with aging, plus
 * the standing progressive-jackpot liability (current meter accrued above each
 * progressive's base).
 *
 * Money fields arrive as decimal strings; `money()` formats them.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import {
  PageHeader,
  StatCard,
  Panel,
  StatusPill,
  money,
} from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/gaming/markers")({
  component: GamingMarkersPage,
});

// ── Types — matches GET /api/hospitality/gaming/markers contract ───────────
interface GamingMarker {
  marker_no: string;
  patron: string;
  patron_ref: string;
  issued: string;
  due: string | null;
  amount: string;
  balance: string;
  age_days: number;
  bucket: string;
  status: string;
}
interface GamingMarkersAging {
  current: string;
  "31_60": string;
  "61_90": string;
  over_90: string;
}
interface GamingMarkersResult {
  property: string;
  as_of: string;
  outstanding_total: string;
  markers: GamingMarker[];
  count: number;
  aging: GamingMarkersAging;
}

// ── Types — matches GET /api/hospitality/gaming/jackpots contract ──────────
interface GamingProgressive {
  name: string;
  game: string;
  current_meter: string;
  base_amount: string;
  as_of: string;
}
interface GamingJackpotsResult {
  property: string;
  total_liability: string;
  count: number;
  progressives: GamingProgressive[];
}

function bucketTone(bucket: string): "exception" | "open" | "neutral" {
  if (bucket === "over_90") return "exception";
  if (bucket === "61_90") return "open";
  return "neutral";
}

function GamingMarkersPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const markersQ = useQuery({
    queryKey: ["hospitality-gaming-markers"],
    queryFn: () => api<GamingMarkersResult>("/api/hospitality/gaming/markers"),
  });
  const jackpotsQ = useQuery({
    queryKey: ["hospitality-gaming-jackpots"],
    queryFn: () => api<GamingJackpotsResult>("/api/hospitality/gaming/jackpots"),
  });

  const data = markersQ.data;
  const jackpots = jackpotsQ.data;
  const loading = markersQ.isLoading || jackpotsQ.isLoading;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Markers & Credit"
        subtitle={
          data
            ? `${data.property}${data.as_of ? ` · as of ${data.as_of}` : ""}`
            : "Outstanding patron markers + progressive jackpot liability"
        }
        right={
          data ? (
            <StatusPill tone={data.count > 0 ? "info" : "success"}>
              {data.count} marker{data.count === 1 ? "" : "s"}
            </StatusPill>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {loading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!loading && data && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              <StatCard label="Outstanding total" value={money(data.outstanding_total, 0)} />
              <StatCard label="Markers" value={String(data.count)} />
            </div>

            {/* Aging buckets */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard label="Current" value={money(data.aging.current, 0)} />
              <StatCard label="31–60" value={money(data.aging["31_60"], 0)} />
              <StatCard label="61–90" value={money(data.aging["61_90"], 0)} />
              <StatCard
                label="90+"
                value={money(data.aging.over_90, 0)}
                tone={Number(data.aging.over_90) > 0 ? "danger" : "default"}
              />
            </div>

            {/* Outstanding markers */}
            <Panel label="Outstanding markers">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Marker</th>
                    <th className="text-left py-1.5 px-3">Patron</th>
                    <th className="text-left py-1.5 px-3">Issued</th>
                    <th className="text-left py-1.5 px-3">Due</th>
                    <th className="text-right py-1.5 px-3">Balance</th>
                    <th className="text-right py-1.5 px-3">Age</th>
                    <th className="text-right py-1.5 pl-3">Bucket</th>
                  </tr>
                </thead>
                <tbody>
                  {data.markers.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-12 text-center text-slate-500 dark:text-slate-400"
                      >
                        No outstanding markers.
                      </td>
                    </tr>
                  )}
                  {data.markers.map((m) => (
                    <tr
                      key={m.marker_no}
                      className="border-t border-slate-100 dark:border-slate-800"
                    >
                      <td className="py-1.5 pr-3 tabular-nums text-xs text-slate-800 dark:text-slate-200">
                        {m.marker_no}
                      </td>
                      <td className="py-1.5 px-3">
                        <div className="text-slate-800 dark:text-slate-200">{m.patron}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {m.patron_ref}
                        </div>
                      </td>
                      <td className="py-1.5 px-3 tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {m.issued}
                      </td>
                      <td className="py-1.5 px-3 tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {m.due ?? "—"}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(m.balance)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {m.age_days}d
                      </td>
                      <td className="py-1.5 pl-3 text-right">
                        <StatusPill tone={bucketTone(m.bucket)}>{m.bucket}</StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            {/* Progressive jackpot liability */}
            {jackpots && (
              <Panel
                label="Progressive jackpot liability"
                right={
                  <span className="text-xs tabular-nums font-medium text-slate-700 dark:text-slate-200">
                    {money(jackpots.total_liability, 0)}
                  </span>
                }
              >
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left py-1.5 pr-3">Name</th>
                      <th className="text-left py-1.5 px-3">Game</th>
                      <th className="text-right py-1.5 px-3">Current meter</th>
                      <th className="text-right py-1.5 pl-3">Base</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jackpots.progressives.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="py-12 text-center text-slate-500 dark:text-slate-400"
                        >
                          No active progressives.
                        </td>
                      </tr>
                    )}
                    {jackpots.progressives.map((p, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-3 text-slate-800 dark:text-slate-200">
                          {p.name}
                        </td>
                        <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300">
                          {p.game}
                        </td>
                        <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                          {money(p.current_meter)}
                        </td>
                        <td className="py-1.5 pl-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                          {money(p.base_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
